'use client';

import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  LinearProgress,
  Stack,
  CircularProgress,
  Alert,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  Article as ArticleIcon,
} from '@mui/icons-material';
import { getMarketSentiment, type MarketSentimentResponse } from '../../lib/api';
import { formatDataTimestamp, formatCacheTimestamp, formatArticleDate } from '../../lib/timezone';

interface NewsArticle {
  title: string;
  description: string;
  source: string;
  publishedAt: string;
  url: string;
}

interface MacroSentiment {
  overallSentiment: number;
  sentimentLabel: 'BEARISH' | 'NEUTRAL' | 'BULLISH';
  breakdown: {
    geopolitical: CategorySentiment;
    monetary: CategorySentiment;
    economic: CategorySentiment;
    market: CategorySentiment;
  };
  aiContext: string;
  topNews: NewsArticle[];
  timestamp: string;
  cached?: boolean;
  cacheStatus?: 'HIT' | 'MISS' | 'STALE_REFRESH'; // v3.4.5: DO-only cache (v3.3.0+)
  cachedAt?: string;
  missDetails?: 'L1_AND_L2' | 'L1_ONLY';

  // v3.0.30+ Enhanced miss tracking
  lastKnownCachedAt?: string;
  timeSinceLastCache?: number;
  missReason?: 'EXPIRED' | 'NEVER_CACHED' | 'UNKNOWN';
}

interface CategorySentiment {
  sentiment: number;
  label: 'BEARISH' | 'NEUTRAL' | 'BULLISH';
  articleCount: number;
}

export function MarketSentimentWidget() {
  const [sentiment, setSentiment] = useState<MacroSentiment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        // Don't show full loading if we already have data
        if (!sentiment) {
          setLoading(true);
        } else {
          setIsRefreshing(true);
        }
        setError(null);

        // Use centralized API client to get cache metadata
        const data = await getMarketSentiment();
        setSentiment(data as unknown as MacroSentiment);

        // If data was stale, show brief refresh indicator
        if ((data as any).stale) {
          setIsRefreshing(true);
          // Auto-hide after 3 seconds
          setTimeout(() => setIsRefreshing(false), 3000);
        } else {
          setIsRefreshing(false);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch market sentiment');
        setIsRefreshing(false);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
    // Add jitter to prevent synchronized refreshes (±2 minutes)
    const jitter = Math.random() * 4 * 60 * 1000 - 2 * 60 * 1000;
    const interval = setInterval(fetchData, 15 * 60 * 1000 + jitter);
    return () => clearInterval(interval);
  }, []);

  const getSentimentColor = (label: string) => {
    switch (label) {
      case 'BULLISH':
        return 'success';
      case 'BEARISH':
        return 'error';
      default:
        return 'warning';
    }
  };

  const getSentimentProgress = (value: number) => {
    return ((value + 1) / 2) * 100; // Convert -1 to +1 → 0 to 100
  };

  if (loading) {
    return (
      <Card>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (error || !sentiment) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error" icon={<ErrorIcon />}>
            {error || 'Failed to load market sentiment'}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" component="div">
              Market Sentiment
            </Typography>
            {isRefreshing && (
              <Chip
                label="Refreshing..."
                size="small"
                color="info"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            )}
          </Box>
          {sentiment.overallSentiment > 0 ? (
            <TrendingUpIcon color="success" />
          ) : (
            <TrendingDownIcon color="error" />
          )}
        </Box>

        {/* Overall Sentiment */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Chip
              label={sentiment.sentimentLabel}
              color={getSentimentColor(sentiment.sentimentLabel) as any}
              sx={{ fontSize: '1rem', fontWeight: 700, px: 2 }}
            />
            <Typography variant="h3" fontWeight={700} color={getSentimentColor(sentiment.sentimentLabel) + '.main'}>
              {sentiment.overallSentiment.toFixed(2)}
            </Typography>
          </Box>

          <LinearProgress
            variant="determinate"
            value={getSentimentProgress(sentiment.overallSentiment)}
            sx={{
              height: 12,
              borderRadius: 6,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: getSentimentColor(sentiment.sentimentLabel) + '.main',
                borderRadius: 6,
              },
            }}
          />
        </Box>

        {/* AI Context */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            AI Analysis
          </Typography>
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            "{sentiment.aiContext}"
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Category Breakdown */}
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          Category Breakdown
        </Typography>
        <Stack spacing={1.5}>
          {Object.entries(sentiment.breakdown).map(([category, data]) => (
            <Box key={category}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                  {category}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={data.label}
                    color={getSentimentColor(data.label) as any}
                    size="small"
                  />
                  <Typography variant="body2" color="text.secondary">
                    {data.sentiment.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
              <LinearProgress
                variant="determinate"
                value={getSentimentProgress(data.sentiment)}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: getSentimentColor(data.label) + '.main',
                    borderRadius: 3,
                  },
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {data.articleCount} articles analyzed
              </Typography>
            </Box>
          ))}
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* News Articles (Collapsible) */}
        <Accordion
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              '& .MuiAccordionSummary-content': {
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              },
            }}
          >
            <ArticleIcon fontSize="small" />
            <Typography variant="subtitle2" fontWeight={700}>
              News Articles ({sentiment.topNews.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              {sentiment.topNews.map((article, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 1.5,
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: 1,
                    borderLeft: '3px solid',
                    borderColor: 'primary.main',
                  }}
                >
                  <Link
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    color="inherit"
                    sx={{ display: 'block', mb: 0.5 }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {article.title}
                    </Typography>
                  </Link>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 1, lineHeight: 1.4 }}
                  >
                    {article.description}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Chip label={article.source} size="small" variant="outlined" />
                    <Typography variant="caption" color="text.secondary">
                      {formatArticleDate(article.publishedAt)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Powered by Dual AI (GPT-OSS + DistilBERT) • On-demand analysis • Sentiment 24h • Articles 12h
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Data sourced: {formatDataTimestamp(sentiment.timestamp)}
          </Typography>
          {sentiment.cachedAt && (
            <Typography
              variant="caption"
              sx={{
                color: sentiment.cacheStatus === 'HIT' ? '#4caf50' : sentiment.cacheStatus === 'STALE_REFRESH' ? '#ff9800' : '#9e9e9e',
                fontWeight: 600
              }}
            >
              Cache: {sentiment.cacheStatus}
              {' '}({formatCacheTimestamp(sentiment.cachedAt)})
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
